/**
 * Intake vocabulary and ranking.
 *
 * An intake item is an inbound Call or Form Submission, mirrored into Supabase
 * (`intake_items`) so the edge between a raw arrival and the Lead it produced has
 * somewhere to live.
 *
 * Each install chooses whether intake is an explicit review gate or an automatic
 * promotion boundary. Cascade keeps every unarchived unresolved arrival in an
 * Actionable Intake desk until a human decides it; the shared service also retains
 * a bounded automatic-promotion option for installs that explicitly configure it.
 *
 * The dispositions and the claim machinery below are unchanged and still
 * load-bearing: they are how promotion stays idempotent across the
 * Supabase/Airtable boundary, whether the promotion was automatic or a person
 * overriding the filter by hand.
 *
 * `intake_items` is the sole workflow authority. `form_submissions.review_status`
 * is maintained as a DEPRECATED compatibility mirror for screens that predate
 * this layer — never read it to make a workflow decision.
 */

export const INTAKE_SOURCE_TYPES = ['call', 'form_submission', 'airtable_record'] as const;
export type IntakeSourceType = (typeof INTAKE_SOURCE_TYPES)[number];

/**
 * Lifecycle of the review itself, distinct from the disposition (the outcome).
 * `claiming` is the atomic-claim state that makes confirmation safe across the
 * Supabase/Airtable boundary — see intake-claim.ts.
 */
export const INTAKE_STATUSES = ['unresolved', 'claiming', 'resolved', 'dismissed'] as const;
export type IntakeStatus = (typeof INTAKE_STATUSES)[number];

/**
 * Terminal outcomes. Mirrors form_submissions_review_status_check exactly
 * (minus its default `needs_review`, which is status `unresolved` here) so the
 * compatibility mirror is a straight copy with no lossy translation.
 *
 * Extending this list is how future outcomes (service_request, applicant,
 * student, mentor, board_nomination) get added — add the value here, add it to
 * the CHECK constraint, and give it a target_type in intake_links if it should
 * produce a record.
 */
export const INTAKE_DISPOSITIONS = [
  'lead_created',
  'linked_to_existing_lead',
  'current_client_project',
  'not_qualified',
  'spam',
  'do_not_contact',
  'duplicate',
  'needs_more_information',
  'auto_routed',
  'reviewed',
  'dismissed',
] as const;
export type IntakeDisposition = (typeof INTAKE_DISPOSITIONS)[number];

/** Dispositions that mean a business record now exists for this intake. */
export const INTAKE_RESOLVING_DISPOSITIONS = [
  'lead_created',
  'linked_to_existing_lead',
  'auto_routed',
  'reviewed',
] as const;

export function isIntakeDisposition(value: string): value is IntakeDisposition {
  return (INTAKE_DISPOSITIONS as readonly string[]).includes(value);
}

export function isResolvingDisposition(value: string): boolean {
  return (INTAKE_RESOLVING_DISPOSITIONS as readonly string[]).includes(value);
}

export function intakeDispositionLabel(value: string): string {
  return value
    .split('_')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------

/**
 * What an intake record's lifecycle looks like to a reader, on the Calls and Form
 * Entries feeds where raw arrivals are read.
 *
 * An intake record's whole job is to stop being one, so the lifecycle is only
 * "what became of it", not a workflow:
 *
 *   new       no Lead yet, and nobody has annotated it
 *   open      no Lead yet, but somebody has written a note on it
 *   resolved  it became, or joined, a Lead
 *   dismissed it was not business
 *
 * `resolved` is what the "This call was turned into a Lead" banner reads. `new`
 * and `open` remain actionable until either a human decision or a deliberately
 * configured automation resolves them.
 *
 * `open` is deliberately NOT rendered as a badge anywhere. It exists so a feed can
 * tell "untouched" from "someone has started on it" without inventing a
 * Reviewed/In Triage stage that would ask the reader to learn a word for something
 * they can already see in the notes.
 */
export const INTAKE_LIFECYCLE_STATES = ['new', 'open', 'resolved', 'dismissed'] as const;
export type IntakeLifecycleState = (typeof INTAKE_LIFECYCLE_STATES)[number];

export interface IntakeLifecycleInput {
  /** `intake_items.status`. */
  status: string;
  /** `intake_items.disposition`. */
  disposition?: string | null;
  /**
   * Whether a human has written a note on the underlying Call or Form Entry.
   *
   * This is the whole "has anyone processed it" signal, and it is derived from
   * `record_notes` rather than stored: a note is the only meaningful
   * interaction an intake record supports that is not already a resolution, so
   * a processed flag would be a second copy of a fact the notes already hold.
   */
  hasNotes?: boolean;
}

export function intakeLifecycleState(input: IntakeLifecycleInput): IntakeLifecycleState {
  if (input.status === 'dismissed') return 'dismissed';
  if (input.status === 'resolved') {
    // A resolved item that somehow carries a non-resolving disposition was
    // ruled out rather than promoted; read it as dismissed so the badge matches
    // what actually happened to it.
    return isResolvingDisposition(input.disposition ?? '') ? 'resolved' : 'dismissed';
  }
  return input.hasNotes ? 'open' : 'new';
}

/** Unresolved work: still in the queue, whether or not anyone has started it. */
export function isIntakeUnresolved(state: IntakeLifecycleState): boolean {
  return state === 'new' || state === 'open';
}

/**
 * The label an intake's terminal state earns on a card or a history row.
 * Unresolved states return undefined — `new` gets its own badge, and `open`
 * gets none at all.
 */
export function intakeResolutionLabel(input: IntakeLifecycleInput): string | undefined {
  const state = intakeLifecycleState(input);
  if (state === 'dismissed') return 'Dismissed';
  if (state !== 'resolved') return undefined;
  return input.disposition === 'linked_to_existing_lead' ? 'Linked to Lead' : 'Lead Created';
}

/**
 * DEPRECATED compatibility mirror. `form_submissions.review_status` predates
 * intake_items and is still read by the shipped Forms feed filters. We keep it
 * in sync on write so those screens keep working; nothing may read it to decide
 * workflow. Remove once the Forms feed reads intake_items directly.
 */
export function intakeStateToReviewStatus(
  status: IntakeStatus,
  disposition: IntakeDisposition | null,
): string {
  if (status === 'unresolved' || status === 'claiming') return 'needs_review';
  return disposition ?? 'needs_review';
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

export interface IntakeRankable {
  ai_confidence: number | null;
  occurred_at: string | null;
  created_at: string;
  /** Loose on purpose: database rows carry plain text. */
  source_type: string;
}

/**
 * Deterministic triage rank. Higher sorts first.
 *
 * AI confidence contributes, but it can never be the whole story: an unscored
 * intake (AI unavailable, or a source we do not score yet) must not sink below
 * scored ones forever, and an old intake must not be starved by a steady drip
 * of newer high-confidence ones. So the score is:
 *
 *   urgency (age, monotonically increasing) + confidence bonus + source weight
 *
 * Age dominates on a long enough horizon — an intake sitting for a week
 * outranks a fresh high-confidence one — which guarantees eventual attention.
 * Ties break on occurred_at ascending (oldest first) in the caller's sort, so
 * ordering is total and stable even when every input is null.
 */
export function intakeRank(item: IntakeRankable, now: Date = new Date()): number {
  const occurred = Date.parse(item.occurred_at ?? item.created_at);
  const ageHours = Number.isFinite(occurred)
    ? Math.max(0, (now.getTime() - occurred) / 3_600_000)
    : 0;

  // Age is the floor guarantee. Capped at 30 days so a stuck item cannot
  // dominate forever, but it still outranks any confidence bonus after ~2 days.
  const urgency = Math.min(ageHours, 24 * 30);

  // Confidence is worth up to 48 "hours" of urgency — enough to lead the queue
  // on arrival, not enough to outrank genuinely aged work.
  const confidence = item.ai_confidence ?? 0;
  const confidenceBonus = Math.max(0, Math.min(1, confidence)) * 48;

  // A missed call decays faster in value than a form submission: the caller is
  // gone and expects a callback. Small nudge, not a reordering.
  const sourceWeight = item.source_type === 'call' ? 6 : 0;

  return urgency + confidenceBonus + sourceWeight;
}

/** Sort unresolved intake for the queue: rank desc, then oldest first. */
export function sortIntakeForQueue<T extends IntakeRankable>(items: T[], now: Date = new Date()): T[] {
  return [...items].sort((a, b) => {
    const diff = intakeRank(b, now) - intakeRank(a, now);
    if (diff !== 0) return diff;
    const aAt = a.occurred_at ?? a.created_at;
    const bAt = b.occurred_at ?? b.created_at;
    return aAt.localeCompare(bAt);
  });
}
